import { useFragment, useReadQuery } from '@apollo/client';
import { AvatarGroup } from '@synoptic/ui-kit/avatar-group/avatar-group.js';
import { Separator } from '@synoptic/ui-kit/separator/separator.tsx';
import { useParams } from 'react-router';
import { gql } from '@/__generated__';
import { TournamentState } from '@/__generated__/graphql';
import { LayoutCard } from '@/components/layout/card';
import { S3Avatar } from '@/components/s3/user/s3-avatar';
import { TextEditor } from '@/components/text-editor/text-editor';
import { LatestSubmissions } from '@/components/tournaments/submissions/latest-submissions.tsx';
import { formatDateWithMs } from '@/date-utils/format-date-with-ms.ts';
import { formatDateShort } from '@/date-utils/format-date.ts';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths';
import { TournamentMedals } from '@/routes/_app.p.tournaments_.$id/tournament-medal/tournament-medals.tsx';
import { EmptyTournamentSubmissions } from '../_app.p.tournaments_.$id.submissions/empty-state';
import { useTournamentLoaderData } from '../_app.p.tournaments_.$id/loader';
import { NotSubmittedAlert } from '../_app.p.tournaments_.$id/not-submitted-alert';
import {
  sectionLabel,
  subSectionLabel,
} from '../_app.p.tournaments_.$id/page.css';
import { container, section, subSection } from './page.css';

const TournamentOverviewFragment = gql(`
  fragment TournamentOverviewFragment on Tournament {
    id
    description
    rules
    state
    label
    participants(limit: 5, nextToken: null) {
      items {
        id
        submissionsCount
        user {
          id
          fullName
          avatar {
            key
          }
        }
      }
      nextToken
    }
    participantsCount
    medals {
     ...MedalCardFragment
    }
    deadline
    mySubmissions {
      id
    }
  }
  `);

export const TournamentOverview = () => {
  const id = useParams().id as string;
  const { submissionsQueryRef } = useTournamentLoaderData();

  const { data: overviewData, complete } = useFragment({
    fragment: TournamentOverviewFragment,
    fragmentName: 'TournamentOverviewFragment',
    from: {
      __typename: 'Tournament',
      id,
    },
  });

  const { data: submissionsData } = useReadQuery(submissionsQueryRef);

  if (!submissionsData.tournament || !complete) return null;

  const submissions = submissionsData.tournament.submissions.items;

  return (
    <div className={container}>
      <LayoutCard className={section}>
        <div className={subSection}>
          <h3 className={sectionLabel}>Overview</h3>
          <TextEditor
            namespace="tournament-overview"
            editable={false}
            initialState={overviewData.description}
          />
        </div>

        <Separator />

        {overviewData.medals.length > 0 ? (
          <>
            <div className={subSection}>
              <TournamentMedals medals={overviewData.medals} />
            </div>
            <Separator />
          </>
        ) : null}

        <div className={subSection}>
          <div className={subSectionLabel}>
            {overviewData.participantsCount} Participants
          </div>

          {overviewData.participants.items.length > 0 ? (
            <AvatarGroup
              label={
                <Link to={paths.makeParticipants(overviewData.id)}>
                  view all
                </Link>
              }
            >
              {overviewData.participants.items.slice(0, 5).map((p) => (
                <S3Avatar
                  key={p.user.id}
                  userId={p.user.id}
                  size="small"
                  s3Key={p.user.avatar?.key}
                />
              ))}
            </AvatarGroup>
          ) : null}

          {overviewData.deadline ? (
            <>
              <span> • Deadline </span>
              <time
                title={formatDateWithMs(new Date(overviewData.deadline))}
                dateTime={overviewData.deadline}
              >
                {formatDateShort(new Date(overviewData.deadline))}
              </time>
            </>
          ) : null}
        </div>
      </LayoutCard>

      {submissions.length > 0 ? (
        <>
          {overviewData.mySubmissions.length === 0 &&
          overviewData.state === TournamentState.Active ? (
            <NotSubmittedAlert />
          ) : null}

          <LatestSubmissions
            tournamentId={overviewData.id}
            submissions={submissions}
          />
        </>
      ) : (
        <EmptyTournamentSubmissions />
      )}
    </div>
  );
};
